// Intentional Pinia store — persists user UI preferences for the workflow builder to
// localStorage. No API calls.
import { defineStore } from "pinia";

export type BuilderViewMode = "canvas" | "stack";

/**
 * Serialization-friendly snapshot of run-history filter state. Stored per project
 * id (project history tab) and per workflow id (builder history mode) so each
 * context remembers its own filters across sessions. Dates are ISO strings
 * because localStorage can't round-trip Date objects.
 */
export interface HistoryFilterSnapshot {
  searchQuery: string;
  statusFilters: string[];
  timeShortcut: string;
  dateFromIso: string | null;
  dateToIso: string | null;
  workflowFilter: string;
  sortBy: string;
  pageSize: number;
}

interface BuilderPrefsState {
  // Selected "Testing Context" project per workflow — survives navigation, refresh, and tab swaps.
  testingContextByWorkflow: Record<string, string>;
  // Canvas vs stack rendering for the builder. `null` means "not yet chosen" — callers
  // can fall back to a sensible default (e.g. stack on small screens).
  viewMode: BuilderViewMode | null;
  // Whether the run-history filters' "Advanced" section is expanded.
  historyFiltersAdvancedOpen: boolean;
  // Grouped run-history view: list root runs only, with children nested under their parent via the
  // expand rail. App-wide view mode (not a per-context filter), on by default — the nested hierarchy
  // is the intended primary view.
  groupChildRuns: boolean;
  // Stacked view: flatten the hierarchy indentation to full-width cards (and hide the connecting
  // lines). App-wide toggle, off by default — the indent is the normal view; this is the escape hatch
  // for deeply-nested workflows where the cumulative indent starves the deepest cards of width.
  flattenStack: boolean;
  // Per-context run-history filter snapshots so each project / workflow remembers
  // what the user last applied. Custom date ranges are dropped at hydrate time —
  // see useHistoryFilters — so stale "May 12 to May 15" ranges don't haunt the
  // user weeks later.
  historyFiltersByProject: Record<string, HistoryFilterSnapshot>;
  historyFiltersByWorkflow: Record<string, HistoryFilterSnapshot>;
}

export const useBuilderPrefsStore = defineStore({
  id: "builderPrefs",
  persist: true,

  state: (): BuilderPrefsState => ({
    testingContextByWorkflow: {},
    viewMode: null,
    historyFiltersAdvancedOpen: false,
    groupChildRuns: true,
    flattenStack: false,
    historyFiltersByProject: {},
    historyFiltersByWorkflow: {},
  }),

  getters: {
    testingContextFor: (state) => (workflowId: string): string | null =>
      state.testingContextByWorkflow[workflowId] ?? null,
    projectHistoryFiltersFor: (state) => (projectId: string): HistoryFilterSnapshot | null =>
      state.historyFiltersByProject[projectId] ?? null,
    workflowHistoryFiltersFor: (state) => (workflowId: string): HistoryFilterSnapshot | null =>
      state.historyFiltersByWorkflow[workflowId] ?? null,
  },

  actions: {
    setTestingContext(workflowId: string, projectId: string) {
      this.testingContextByWorkflow[workflowId] = projectId;
    },
    setViewMode(mode: BuilderViewMode) {
      this.viewMode = mode;
    },
    setHistoryFiltersAdvancedOpen(open: boolean) {
      this.historyFiltersAdvancedOpen = open;
    },
    setGroupChildRuns(value: boolean) {
      this.groupChildRuns = value;
    },
    setFlattenStack(value: boolean) {
      this.flattenStack = value;
    },
    setProjectHistoryFilters(projectId: string, snapshot: HistoryFilterSnapshot) {
      if (!projectId) return;
      this.historyFiltersByProject[projectId] = snapshot;
    },
    setWorkflowHistoryFilters(workflowId: string, snapshot: HistoryFilterSnapshot) {
      if (!workflowId) return;
      this.historyFiltersByWorkflow[workflowId] = snapshot;
    },
  },
});
